import type { AnchorHTMLAttributes, ReactNode } from 'react';
import type { IconNames } from '@workramp/ui';
import type { BannerProps } from './Banner';
import type { OverflowMenuItems } from '../../types';
import { Stack, Icon, Col, usePress } from '@workramp/ui';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { MixPanel } from '~/common';
import StringUtils from '~/common/string-utils';
import { IconList } from '~/features/common';
import { InteractiveCard } from '../InteractiveCard';
import { CardOverflowMenu } from '../OverflowCardMenu';
import { Banner } from './Banner';
import {
  BackgroundImg,
  CardWrapper,
  CardBody,
  IconWrapper,
  Description,
  ActionText,
  CardFooter,
  Subtitle,
  Title,
  FullHeightRow,
  ActionsWrapper,
} from './LearnerCard.styles';

interface LearnerCardMetadata {
  iconName: IconNames;
  text: ReactNode;
}

const MIXPANEL_CARD_CLICK = 'learner_home_page_card_click';

export interface LearnerCardProps {
  title: string;
  subtitle?: string;
  description?: string | null;
  imgUrl?: string | null;
  icon?: ReactNode;
  iconName?: IconNames;
  iconColor?: string;
  metadata?: LearnerCardMetadata[];
  banner?: string;
  bannerVariant?: BannerProps['variant'];
  actionText?: ReactNode;
  actions?: ReactNode;
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  className?: string;
  maxCardHeight?: number;
  maxTextLines?: number;
  overflowMenuItems?: OverflowMenuItems;
  onPress?: () => void;
}

export function LearnerCard({
  title,
  subtitle,
  description,
  imgUrl,
  icon,
  iconName,
  iconColor,
  metadata,
  banner,
  bannerVariant,
  actionText,
  actions,
  href,
  target,
  className,
  maxCardHeight,
  maxTextLines,
  overflowMenuItems = {},
  onPress,
}: LearnerCardProps) {
  const isInteractive = !!(href || onPress);

  const addMixPanel = () => {
    // We only want to track card clicks on the Learner Home Page
    if (window.location.pathname.startsWith('/guides')) {
      MixPanel.track(MIXPANEL_CARD_CLICK, {
        card_title: title,
        is_overdue: banner,
        card_action_text: actionText,
        card_href: href,
      });
    }
  };

  const handleOnPress = () => {
    addMixPanel();
    onPress?.();
  };

  const { pressProps } = usePress({
    onPress: () => {
      addMixPanel();
      if (href) {
        if (target === '_blank') {
          window.open(href, '_blank');
        } else {
          window.location.href = href;
        }
      }
      onPress?.();
    },
  });

  const hasIcon = (iconName && iconColor) || icon;

  const showOverflowMenu = Boolean(Object.keys(overflowMenuItems || {}).length);

  return (
    <CardWrapper className={className}>
      <InteractiveCard href={href} target={target} onPress={handleOnPress} actionText={actionText} />

      {banner && <Banner label={banner} variant={bannerVariant} />}

      {imgUrl && <BackgroundImg src={imgUrl} alt={title} decoding="async" loading="lazy" />}

      <CardBody $hasBackgroundImg={!!imgUrl} $hasIcon={!!hasIcon} $maxCardHeight={maxCardHeight}>
        <Stack size={3}>
          {hasIcon && (
            <IconWrapper role="none">{icon || <Icon name={iconName} color={iconColor} size="lg" />}</IconWrapper>
          )}

          <div>
            {subtitle && (
              <Subtitle isExpandable size={0} maxLines={1} {...pressProps}>
                {subtitle}
              </Subtitle>
            )}

            <Title isExpandable weight="semibold" maxLines={maxTextLines || 3} data-testid="text-title" {...pressProps}>
              {title}
            </Title>
          </div>

          {metadata && (
            <IconList>
              {metadata.map((item) => (
                <IconList.Item key={`${item.text?.toString()}_${item.iconName}`}>
                  <IconList.Icon name={item.iconName} />
                  <IconList.Text isInteractive={isInteractive} {...pressProps}>
                    {item.text}
                  </IconList.Text>
                </IconList.Item>
              ))}
            </IconList>
          )}

          {description && (
            <Description isExpandable maxLines={maxTextLines || 3} size={1} density="xl" {...pressProps}>
              {StringUtils.prettifyEscapedString(sanitizeUserInput(description))}
            </Description>
          )}
        </Stack>
      </CardBody>

      {(showOverflowMenu || actionText || actions) && (
        <CardFooter>
          <FullHeightRow align="center" justify="space-between">
            <Col>{actionText && <ActionText size={1}>{actionText}</ActionText>}</Col>
            {actions && (
              <Col xs="auto">
                <ActionsWrapper>
                  {actions}
                  {showOverflowMenu && <CardOverflowMenu overflowMenuItems={overflowMenuItems} />}
                </ActionsWrapper>
              </Col>
            )}
          </FullHeightRow>
        </CardFooter>
      )}
    </CardWrapper>
  );
}
